import { useState, useEffect } from 'react';
import { fetchIndividualEmails } from 'api/emailHistory/individualEmailsApi';

/**
 * @hook useIndividualEmails
 * @description Fetches individual emails data with pagination, sorting, and filtering
 * @param {Object} filters - Filter parameters from URL state
 * @returns {Object} Object containing individual emails data, stats, loading state, and error
 */
export function useIndividualEmails(filters) {
  const [individualEmails, setIndividualEmails] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [stats, setStats] = useState({
    totalEmails: 0,
    delivered: 0,
    opens: 0,
    linkClicks: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadIndividualEmails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Convert date range to ISO strings if needed
        let dateFrom = filters.dateFrom;
        let dateTo = filters.dateTo;

        // If dateFrom/dateTo are Date objects, convert to ISO strings
        if (dateFrom instanceof Date) {
          dateFrom = dateFrom.toISOString();
        }
        if (dateTo instanceof Date) {
          dateTo = dateTo.toISOString();
        }

        const data = await fetchIndividualEmails({
          page: filters.page,
          pageSize: filters.pageSize,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          type: 'all', // Show both campaign and individual emails
          status: filters.status || null,
          search: filters.search || null,
          dateFrom,
          dateTo,
          contactId: filters.contactId || null,
          bulkEmailId: filters.bulkEmailId || null,
        });

        setIndividualEmails(data.rows || []);
        setTotalRows(data.totalRows || 0);

        // Use stats from API response (calculated server-side across all matching records)
        const apiStats = data.stats || {};
        setStats({
          totalEmails: apiStats.totalEmails ?? data.totalRows ?? 0,
          delivered: apiStats.delivered ?? 0,
          opens: apiStats.opens ?? 0,
          linkClicks: apiStats.linkClicks ?? 0,
        });
      } catch (err) {
        console.error('Error fetching individual emails:', err);
        setError(err.message || 'Failed to load individual emails');
        setIndividualEmails([]);
        setTotalRows(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadIndividualEmails();
  }, [
    filters.page,
    filters.pageSize,
    filters.sortBy,
    filters.sortOrder,
    filters.status,
    filters.search,
    filters.dateFrom,
    filters.dateTo,
    filters.contactId,
    filters.bulkEmailId,
  ]);

  return {
    individualEmails,
    totalRows,
    stats,
    isLoading,
    error,
  };
}

